import { NavLink, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/design-system";
import { Icon, type IconName } from "@/components/Icon";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/auth";
import logoWordmark from "@/design-system/assets/logo-wordmark.svg";
import styles from "./SideNav.module.css";

/**
 * SideNav — the role-aware navigation rail.
 *
 * Rendered by <AppShell> only when a user is signed in. On mobile it is
 * a slide-in drawer (opened by the AppShell hamburger); at the desktop
 * breakpoint it becomes the persistent left rail. Nav targets are
 * role-aware: students get the schedule + their bookings, teachers get
 * the management dashboard.
 */

interface NavItem {
  to: string;
  labelKey: string;
  icon: IconName;
}

const STUDENT_LINKS: NavItem[] = [
  { to: "/calendar", labelKey: "nav.schedule", icon: "calendar" },
  { to: "/my-reservations", labelKey: "nav.myBookings", icon: "check" },
  { to: "/membership", labelKey: "nav.myMembership", icon: "heart" },
  { to: "/settings", labelKey: "nav.settings", icon: "settings" },
];

const TEACHER_LINKS: NavItem[] = [
  { to: "/teacher", labelKey: "nav.dashboard", icon: "activity" },
  { to: "/teacher/analytics", labelKey: "nav.analytics", icon: "trending-up" },
  { to: "/teacher/runbooks", labelKey: "nav.runbooks", icon: "compass" },
  { to: "/settings", labelKey: "nav.settings", icon: "settings" },
];

export function SideNav({
  id,
  open = false,
  onClose,
}: {
  /** DOM id, referenced by the AppShell hamburger's `aria-controls`. */
  id?: string;
  /** Mobile drawer open state; ignored on desktop where the rail is fixed. */
  open?: boolean;
  /** Called when a nav target is chosen or the drawer is dismissed. */
  onClose?: () => void;
}) {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isTeacher = currentUser?.role === "teacher";
  const links = isTeacher ? TEACHER_LINKS : STUDENT_LINKS;
  const email = currentUser?.email ?? "";

  async function handleLogout() {
    await logout();
    onClose?.();
    navigate("/login", { replace: true });
  }

  return (
    <nav
      id={id}
      className={open ? `${styles.nav} ${styles.navOpen}` : styles.nav}
      aria-label={t("nav.ariaPrimary")}
    >
      <div className={styles.brand}>
        <img src={logoWordmark} alt="TheCore Studio" className={styles.brandMark} />
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={t("nav.closeMenu")}
          data-testid="sidenav-close"
        >
          <Icon name="x" size={20} />
        </button>
      </div>

      <div className={styles.links}>
        {links.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) =>
              isActive ? `${styles.link} ${styles.linkActive}` : styles.link
            }
          >
            <Icon name={item.icon} size={20} />
            {t(item.labelKey)}
          </NavLink>
        ))}
      </div>

      <div className={styles.spacer} />

      <div style={{ padding: "0 4px 12px" }}>
        <LanguageSwitcher style={{ width: "100%" }} />
      </div>

      <div className={styles.account}>
        <Avatar name={email || t("profile.member")} size={36} />
        <div className={styles.accountText}>
          <div className={styles.accountEmail}>{email}</div>
          <div className={styles.accountRole}>
            {isTeacher ? t("nav.roleTeacher") : t("nav.roleStudent")}
          </div>
        </div>
        <button
          type="button"
          className={styles.logout}
          onClick={handleLogout}
          data-testid="sidenav-logout"
        >
          {t("common.signOut")}
        </button>
      </div>
    </nav>
  );
}
